import {
  AtSign,
  Banknote,
  Megaphone,
  Check,
  Clock,
  Combine,
  KeyRound,
  FileSignature,
  Inbox,
  MailX,
  MessageSquare,
  Server,
  ShieldCheck,
  Wallet,
  X,
} from 'lucide-react';

import { SupportIcon } from '@/components/ui/support-icon';
import { Link } from 'react-router-dom';

import { motivoDeCasilla } from '@/features/campaigns/casilla/motivo-de-casilla';
import { motivoDeRebote } from '@/features/campaigns/rebotes/motivo-legible';
import type { AppNotification } from '@/features/notifications/api';
import { textoDeMonto } from '@/features/partners/autorizaciones-form';

import { TiempoRelativo } from './TiempoRelativo';

const ICON_BY_KIND = {
  assignment_accepted: Check,
  assignment_declined: X,
  assignment_stale_assigner: Clock,
  assignment_stale_assignee: Clock,
  chat_message: MessageSquare,
  chat_mention: AtSign,
  client_env_alert: Server,
  collections_payment_reported: Banknote,
  collections_payment_confirmed: Banknote,
  collections_payment_rejected: Banknote,
  collections_installment_overdue: Clock,
  collections_installment_default: Clock,
  collections_settlement_paid: Wallet,
  collections_contract_request: FileSignature,
  collections_contract_loaded: FileSignature,
  client_ticket_new: SupportIcon,
  client_ticket_message: SupportIcon,
  client_ticket_assigned: SupportIcon,
  partner_ticket_new: SupportIcon,
  partner_ticket_message: SupportIcon,
  partner_plan_change: SupportIcon,
  work_authorization_signed: FileSignature,
  work_invoice_requested: FileSignature,
  work_payment_informed: FileSignature,
  social_touches_due: Megaphone,
  campaign_email_bounced: MailX,
  campaign_sender_down: MailX,
  campaign_opt_out: MailX,
  campaign_opt_out_doubt: MailX,
  task_mention: AtSign,
  prospect_merged: Combine,
  prospect_access_requested: KeyRound,
  prospect_access_decided: KeyRound,
  privacidad_retencion: ShieldCheck,
  lead_web_received: Inbox,
} as const;

const ICON_COLOR_BY_KIND: Record<AppNotification['kind'], string> = {
  assignment_accepted: 'text-emerald-400',
  assignment_declined: 'text-rose-300',
  assignment_stale_assigner: 'text-amber-300',
  assignment_stale_assignee: 'text-amber-300',
  chat_message: 'text-sky-300',
  chat_mention: 'text-sky-300',
  client_env_alert: 'text-amber-300',
  collections_payment_reported: 'text-sky-300',
  collections_payment_confirmed: 'text-emerald-400',
  collections_payment_rejected: 'text-rose-300',
  collections_installment_overdue: 'text-amber-300',
  collections_installment_default: 'text-rose-300',
  collections_settlement_paid: 'text-emerald-400',
  collections_contract_request: 'text-sky-300',
  collections_contract_loaded: 'text-emerald-400',
  client_ticket_new: 'text-amber-300',
  client_ticket_message: 'text-sky-300',
  // Ámbar: quedar a cargo es algo para hacer, no una novedad.
  client_ticket_assigned: 'text-amber-300',
  partner_ticket_new: 'text-amber-300',
  partner_ticket_message: 'text-sky-300',
  // Ámbar: hay que revisar el abono y reajustar si hace falta.
  partner_plan_change: 'text-amber-300',
  // Ámbar: un trabajo firmado arranca ahora y un pedido de factura espera la factura.
  work_authorization_signed: 'text-amber-300',
  work_invoice_requested: 'text-amber-300',
  work_payment_informed: 'text-amber-300',
  // Ámbar: un toque vencido es accionable ahora, no una novedad informativa.
  social_touches_due: 'text-amber-300',
  // Rosa: la campaña no envía nada hasta que alguien decida.
  campaign_email_bounced: 'text-rose-300',
  campaign_sender_down: 'text-rose-300',
  campaign_opt_out: 'text-rose-300',
  campaign_opt_out_doubt: 'text-amber-300',
  prospect_merged: 'text-sky-300',
  prospect_access_requested: 'text-amber-300',
  prospect_access_decided: 'text-emerald-400',
  task_mention: 'text-sky-300',
  // Ámbar: hay datos personales vencidos esperando una confirmación.
  privacidad_retencion: 'text-amber-300',
  lead_web_received: 'text-sky-300',
};

/** Estado del entorno → texto para la alerta de cliente (Anexo H). */
function envStateLabel(to?: string): string {
  switch (to) {
    case 'paused':
      return 'quedó pausado';
    case 'unreachable':
      return 'no responde';
    case 'healthy':
      return 'se recuperó';
    case 'degraded':
      return 'está degradado';
    default:
      return `pasó a ${to ?? 'estado desconocido'}`;
  }
}

function describe(n: AppNotification): { text: string; reason?: string | null } {
  const title = n.payload.task_title ?? 'una tarea';
  const actor = n.payload.actor_name ?? 'Alguien';
  const assignee = n.payload.assignee_name ?? 'el asignado';
  switch (n.kind) {
    case 'assignment_accepted':
      return { text: `${actor} aceptó «${title}»` };
    case 'assignment_declined':
      return { text: `${actor} rechazó «${title}»`, reason: n.payload.reason };
    case 'assignment_stale_assigner':
      return { text: `${assignee} no respondió «${title}» hace +3 días` };
    case 'chat_message': {
      const sender = n.payload.sender_name ?? 'Alguien';
      return { text: `${sender} te escribió`, reason: n.payload.preview };
    }
    case 'chat_mention': {
      // «Te mencionó» y no «te escribió»: en un canal el mensaje no es solo
      // para vos, y saber DÓNDE es la mitad del aviso.
      const sender = n.payload.sender_name ?? 'Alguien';
      const canal = (n.payload.channel_name ?? '').trim();
      const donde = canal ? (canal.startsWith('#') ? canal : `#${canal}`) : 'un canal';
      return { text: `${sender} te mencionó en ${donde}`, reason: n.payload.preview };
    }
    case 'task_mention': {
      const sender = n.payload.sender_name ?? 'Alguien';
      return { text: `${sender} te mencionó en «${title}»`, reason: n.payload.preview };
    }
    case 'client_env_alert': {
      const env = n.payload.name ?? n.payload.slug ?? 'Un entorno';
      return { text: `${env} ${envStateLabel(n.payload.to)}` };
    }
    case 'collections_payment_reported': {
      const who = n.payload.prospect_name ?? 'un cliente';
      return { text: `Cobro informado de ${who} · USD ${n.payload.amount_usd ?? '—'}` };
    }
    case 'collections_payment_confirmed':
      return { text: `Cobro acreditado · USD ${n.payload.amount_usd ?? '—'} — comisión generada` };
    case 'collections_payment_rejected':
      return { text: 'Un cobro tuyo fue rechazado', reason: n.payload.reason };
    case 'collections_installment_overdue':
      return {
        text: `Cuota vencida · ${n.payload.item_title ?? 'abono'} · USD ${n.payload.amount_usd ?? '—'}`,
      };
    case 'collections_installment_default': {
      const contract = n.payload.contract_title ?? 'un contrato';
      return { text: `Mora superada en ${contract} — revisar cobranza` };
    }
    case 'collections_settlement_paid':
      return { text: `Liquidación pagada · USD ${n.payload.total_usd ?? '—'}` };
    case 'collections_contract_request': {
      const client = n.payload.prospect_name ?? 'un cliente';
      return { text: `Piden cargar el contrato de ${client}`, reason: n.payload.note };
    }
    case 'collections_contract_loaded': {
      const client = n.payload.prospect_name ?? 'tu cliente';
      return { text: `Contrato de ${client} cargado — ya podés hacer el seguimiento` };
    }
    case 'client_ticket_new': {
      const titulo = n.payload.ticket_title ?? 'un pedido';
      const quien = n.payload.author_name ?? 'el cliente';
      return {
        text:
          n.payload.urgency === 'alta'
            ? `${quien} abrió «${titulo}» y lo marcó urgente`
            : `${quien} abrió «${titulo}»`,
      };
    }
    case 'client_ticket_message': {
      const quien = n.payload.author_name ?? 'El cliente';
      const titulo = n.payload.ticket_title ?? 'un ticket';
      return { text: `${quien} escribió en «${titulo}»` };
    }
    case 'partner_ticket_new': {
      const partner = n.payload.tenant_name ?? 'Un partner';
      const titulo = n.payload.ticket_title ?? 'un pedido';
      return { text: `${partner} abrió un ticket: ${titulo}` };
    }
    case 'partner_ticket_message': {
      const partner = n.payload.tenant_name ?? 'Un partner';
      const titulo = n.payload.ticket_title ?? 'un ticket';
      const text = `${partner} respondió en «${titulo}»`;
      return n.payload.excerpt ? { text: `${text}: ${n.payload.excerpt}` } : { text };
    }
    case 'partner_plan_change': {
      const partner = n.payload.tenant_name ?? 'Un partner';
      const quien = n.payload.actor_name ?? 'el cliente';
      const plan = n.payload.plan_label ?? n.payload.plan ?? 'otro plan';
      const horas = n.payload.monthly_hours != null ? ` (${n.payload.monthly_hours} h/mes)` : '';
      const desde = n.payload.period_label ?? 'el próximo período';
      return n.payload.accion === 'anulado'
        ? { text: `${partner}: ${quien} anuló el cambio a «${plan}»${horas} previsto para ${desde}` }
        : { text: `${partner}: ${quien} programó pasar a «${plan}»${horas} desde ${desde}` };
    }
    case 'work_authorization_signed': {
      const partner = n.payload.tenant_name ?? 'Un partner';
      const titulo = n.payload.title ?? 'un trabajo';
      const total = n.payload.total != null ? ` por ${textoDeMonto(n.payload.total, n.payload.currency ?? 'USD')}` : '';
      return { text: `${partner} autorizó «${titulo}»${total}` };
    }
    case 'work_invoice_requested': {
      const partner = n.payload.tenant_name ?? 'Un partner';
      const titulo = n.payload.title ?? 'un trabajo';
      const text = `${partner} pidió la factura de «${titulo}»`;
      return n.payload.note ? { text: `${text}: ${n.payload.note}` } : { text };
    }
    case 'work_payment_informed': {
      const partner = n.payload.tenant_name ?? 'Un partner';
      const titulo = n.payload.title ?? 'un trabajo';
      const monto = n.payload.amount != null ? ` · ${textoDeMonto(n.payload.amount, n.payload.currency ?? 'USD')}` : '';
      const factura = n.payload.quiere_factura ? ' · pide factura' : '';
      return { text: `${partner} informó el pago de «${titulo}»${monto}${factura}` };
    }
    case 'client_ticket_assigned': {
      const titulo = n.payload.ticket_title ?? 'un pedido de soporte';
      return { text: `Quedaste a cargo de «${titulo}»` };
    }
    case 'social_touches_due': {
      // El detalle de cada toque está en la sección Campañas de este mismo
      // panel y en la bandeja del día; acá alcanza con la cuenta y el enlace.
      const total = n.payload.total ?? 0;
      const detenidas = n.payload.total_detenidas ?? 0;
      const toques =
        total === 1 ? '1 toque de campaña para mandar' : `${total} toques de campaña para mandar`;
      const paradas =
        detenidas === 1
          ? '1 campaña detenida esperando decisión'
          : `${detenidas} campañas detenidas esperando decisión`;
      if (total > 0 && detenidas > 0) return { text: `Tenés ${toques}`, reason: paradas };
      return { text: detenidas > 0 && total === 0 ? `Tenés ${paradas}` : `Tenés ${toques}` };
    }
    case 'prospect_access_requested': {
      const quien = n.payload.requester_name ?? 'Alguien';
      const text = `${quien} pide ver «${n.payload.prospect_name ?? 'una ficha'}»`;
      return n.payload.message ? { text, reason: n.payload.message } : { text };
    }
    case 'prospect_access_decided': {
      const ficha = n.payload.prospect_name ?? 'la ficha';
      const quien = n.payload.decided_by_name ?? 'El dueño';
      if (!n.payload.approved) return { text: `Sin acceso a «${ficha}»`, reason: `${quien} no lo otorgó` };
      const permiso = n.payload.permission === 'edit' ? 'edición' : 'lectura';
      return { text: `Ya podés ver «${ficha}»`, reason: `${quien} te dio acceso de ${permiso}` };
    }
    case 'prospect_merged': {
      const ficha = n.payload.survivor_name ?? 'una ficha';
      if (!n.payload.by_name) return { text: `Fichas unificadas en «${ficha}»` };
      return { text: `Fichas unificadas en «${ficha}»`, reason: `Lo hizo ${n.payload.by_name}` };
    }
    case 'campaign_email_bounced': {
      const casilla = n.payload.casilla ?? 'una casilla';
      const motivo = motivoDeRebote(n.payload.codigo ?? null, n.payload.diagnostico ?? null);
      const campana = n.payload.campaign_name ?? 'una campaña';
      if (n.payload.rol === 'prospecto') {
        const quien = n.payload.campaign_owner_name ?? 'otro vendedor';
        return {
          text: `Rebotó un correo a ${n.payload.prospect_name ?? 'tu prospecto'}`,
          reason: `${casilla} (${motivo}). «${campana}» de ${quien} quedó detenida`,
        };
      }
      return { text: `Campaña detenida: ${campana}`, reason: `${casilla} rebotó (${motivo})` };
    }
    case 'campaign_sender_down': {
      const campana = n.payload.campaign_name ?? 'una campaña';
      const casilla = n.payload.sender_email ?? 'Su casilla';
      return { text: `Campaña pausada: ${campana}`, reason: `${casilla} ${motivoDeCasilla(n.payload.motivo)}` };
    }
    case 'campaign_opt_out': {
      const prospecto = n.payload.prospect_name ?? 'Un prospecto';
      return {
        text: `${prospecto} pidió la baja`,
        reason: 'Campañas frenadas, contacto bloqueado y confirmación enviada como Departamento Comercial',
      };
    }
    case 'campaign_opt_out_doubt': {
      const prospecto = n.payload.prospect_name ?? 'Un prospecto';
      return {
        text: `¿${prospecto} pidió la baja?`,
        reason: 'Frenamos sus campañas y no se respondió nada: revisá la respuesta y decidí',
      };
    }
    case 'lead_web_received': {
      const quien = n.payload.nombre ?? 'Alguien';
      return {
        text: `${quien} escribió desde dendev.ar`,
        reason: n.payload.asunto ?? n.payload.preview,
      };
    }
    case 'privacidad_retencion': {
      const n24 = n.payload.cantidad ?? 0;
      return {
        text: n24 === 1
          ? '1 prospecto superó los 24 meses sin interacción'
          : `${n24} prospectos superaron los 24 meses sin interacción`,
        reason: 'Revisalos y confirmá la anonimización en Privacidad',
      };
    }
    default:
      return { text: `Tu asignación de «${title}» sigue sin respuesta` };
  }
}

export function NotificationItem({
  notification,
  onActivate,
}: {
  notification: AppNotification;
  onActivate: () => void;
}) {
  const Icon = ICON_BY_KIND[notification.kind] ?? Clock;
  const { text, reason } = describe(notification);
  return (
    <li className="hover:bg-surface-hi/40 border-border-dim/40 border-b last:border-0">
      <Link
        to={notification.link ?? '/tareas'}
        onClick={onActivate}
        className="flex items-center gap-2 px-3.5 py-2 text-caption"
      >
        <Icon className={`size-3.5 shrink-0 ${ICON_COLOR_BY_KIND[notification.kind]}`} />
        <span className="text-foreground flex-1 truncate">
          {text}
          {reason ? <span className="text-fg-dim"> · {reason}</span> : null}
        </span>
        <TiempoRelativo iso={notification.created_at} />
      </Link>
    </li>
  );
}
